/**
 * Training API Hooks
 *
 * TanStack Query hooks for training jobs.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listTrainingJobsApiV1TrainingJobsGet,
  createTrainingJobApiV1TrainingJobsPost,
  getTrainingJobApiV1TrainingJobsJobIdGet,
  cancelTrainingJobApiV1TrainingJobsJobIdDelete,
} from '@/api';
import type { TrainingJobCreate } from '@/api';
import { useAuthEnabled } from './useAuthQuery';

// Query keys factory
export const trainingJobKeys = {
  all: ['trainingJobs'] as const,
  lists: () => [...trainingJobKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) =>
    [...trainingJobKeys.lists(), filters] as const,
  details: () => [...trainingJobKeys.all, 'detail'] as const,
  detail: (id: string) => [...trainingJobKeys.details(), id] as const,
};

/**
 * List all training jobs.
 */
export function useTrainingJobs(options?: {
  filters?: { status?: string };
  enabled?: boolean;
}) {
  const { enabled: authEnabled } = useAuthEnabled(options?.enabled ?? true);

  return useQuery({
    queryKey: trainingJobKeys.list(options?.filters),
    queryFn: async () => {
      const response = await listTrainingJobsApiV1TrainingJobsGet({
        query: options?.filters,
      });
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to fetch training jobs'
        );
      }
      return response.data?.items ?? [];
    },
    enabled: authEnabled,
  });
}

/**
 * Get a single training job.
 */
export function useTrainingJob(jobId: string) {
  const { enabled: authEnabled } = useAuthEnabled(Boolean(jobId));

  return useQuery({
    queryKey: trainingJobKeys.detail(jobId),
    queryFn: async () => {
      const response = await getTrainingJobApiV1TrainingJobsJobIdGet({
        path: { job_id: jobId },
      });
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to fetch training job'
        );
      }
      return response.data;
    },
    enabled: authEnabled,
    // Poll for in-progress jobs
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'pending' || status === 'training') {
        return 5000; // Poll every 5s while in progress
      }
      return false;
    },
  });
}

/**
 * Create a new training job.
 */
export function useCreateTrainingJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: TrainingJobCreate) => {
      const response = await createTrainingJobApiV1TrainingJobsPost({
        body: data,
      });
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to create training job'
        );
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trainingJobKeys.lists() });
    },
  });
}

/**
 * Cancel a training job.
 */
export function useCancelTrainingJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (jobId: string) => {
      const response = await cancelTrainingJobApiV1TrainingJobsJobIdDelete({
        path: { job_id: jobId },
      });
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to cancel training job'
        );
      }
      return response.data;
    },
    onSuccess: (_, jobId) => {
      queryClient.invalidateQueries({
        queryKey: trainingJobKeys.detail(jobId),
      });
      queryClient.invalidateQueries({ queryKey: trainingJobKeys.lists() });
    },
  });
}
